import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_STEP_CONDITION_ORDER,
  MINUTES_PER_DAY,
} from '../model/use-campaign-add-step'
import type { CampaignStepWaitOption } from '../model/use-campaign-add-step'
import type { UseCampaignComposerResult } from '../model/use-campaign-composer'
import { CampaignChoiceChip } from './campaign-choice-chip'
import { CampaignCustomDaysInput } from './campaign-custom-days-input'

const MINUTES_PER_HOUR = 60

/** A preset reads as the picker words it; a stored off-preset wait in its own largest whole unit. */
function waitLabel(t: TFunction, option: CampaignStepWaitOption): string {
  if (option.preset != null) {
    return t(`campaigns.sequence.addStep.delay.${option.preset}`)
  }
  if (option.minutes % MINUTES_PER_DAY === 0) {
    return t('campaigns.composer.waitDays', {
      count: option.minutes / MINUTES_PER_DAY,
    })
  }
  if (option.minutes % MINUTES_PER_HOUR === 0) {
    return t('campaigns.composer.waitHours', {
      count: option.minutes / MINUTES_PER_HOUR,
    })
  }
  return t('campaigns.composer.waitMinutes', { count: option.minutes })
}

interface CampaignStepWaitRowProps {
  composer: UseCampaignComposerResult
}

/** The add picker's "Only if" chips, over the step's stored conditions. */
function ConditionRow({ composer }: CampaignStepWaitRowProps) {
  const { t } = useTranslation()
  const conditions = composer.conditions ?? []
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-warm-500">
        {t('campaigns.sequence.addStep.onlyIf')}
      </span>
      {CAMPAIGN_STEP_CONDITION_ORDER.map((condition) => (
        <CampaignChoiceChip
          key={condition}
          label={t(`campaigns.sequence.addStep.condition.${condition}`)}
          selected={conditions.includes(condition)}
          onClick={() => composer.toggleCondition(condition)}
        />
      ))}
      <CampaignChoiceChip
        label={t('campaigns.sequence.addStep.alwaysSend')}
        selected={conditions.length === 0}
        onClick={composer.setAlwaysSend}
      />
    </div>
  )
}

/**
 * How long the step waits after the one before it, and on what condition —
 * the same chips the add picker's "When" and "Only if" rows offer, so an edit
 * can change what adding chose.
 */
export function CampaignStepWaitRow({ composer }: CampaignStepWaitRowProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1.5 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-warm-500">
          {t('campaigns.composer.wait')}
        </span>
        {composer.waitOptions.map((option) => (
          <CampaignChoiceChip
            key={option.minutes}
            label={waitLabel(t, option)}
            selected={
              composer.customDays == null &&
              composer.delayMinutes === option.minutes
            }
            onClick={() => composer.setDelayMinutes(option.minutes)}
          />
        ))}
        <CampaignChoiceChip
          label={t('campaigns.sequence.addStep.delay.custom')}
          selected={composer.customDays != null}
          onClick={composer.pickCustom}
        />
        {composer.customDays != null && (
          <CampaignCustomDaysInput
            value={composer.customDays}
            onChange={composer.setCustomDays}
            invalid={composer.customDays !== '' && composer.customDaysInvalid}
          />
        )}
      </div>
      {composer.conditions != null && <ConditionRow composer={composer} />}
      <p className="text-xs text-warm-500">
        {t('campaigns.composer.waitNote')}
      </p>
    </div>
  )
}
